import type {MissingSubIssue} from '../../api/common-contracts'
import type {MemexItem} from '../../api/memex-items/contracts'
import type {PageInfo} from '../../api/memex-items/paginated-views'
import {isPlainObject} from '../../helpers/replace-equal-deep'
import {createMemexItemModel} from '../../models/memex-item-model'
import {safeSessionStorage} from '../../platform/safe-session-storage'
import type {MemexSubIssuesQueryData} from '../../state-providers/memex-items/queries/types'

/** Raw sub-issues data stored in sessionStorage (plain JSON, not model instances). */
type CachedSubIssuesEntry = {
  nodes: Array<MemexItem>
  pageInfo: PageInfo
  missingSubIssues?: Array<MissingSubIssue>
}

/** Map of nwoReference → cached sub-issues entry. Stored as a single sessionStorage key. */
export type SubIssuesCacheMap = Record<string, CachedSubIssuesEntry>

export const SUB_ISSUES_CACHE_STORAGE_KEY = 'projects.subIssuesCache'

/** Removes all cached sub-issues data from sessionStorage. Intended for test cleanup only. */
export function clearSubIssuesCacheForTests(): void {
  safeSessionStorage.removeItem(SUB_ISSUES_CACHE_STORAGE_KEY)
}

export function readSubIssuesCacheMap(): SubIssuesCacheMap {
  const raw = safeSessionStorage.getItem(SUB_ISSUES_CACHE_STORAGE_KEY)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    if (!isPlainObject(parsed)) {
      safeSessionStorage.removeItem(SUB_ISSUES_CACHE_STORAGE_KEY)
      return {}
    }
    return parsed as SubIssuesCacheMap
  } catch {
    safeSessionStorage.removeItem(SUB_ISSUES_CACHE_STORAGE_KEY)
    return {}
  }
}

function writeCacheMap(map: SubIssuesCacheMap): void {
  try {
    if (Object.keys(map).length === 0) {
      safeSessionStorage.removeItem(SUB_ISSUES_CACHE_STORAGE_KEY)
    } else {
      safeSessionStorage.setItem(SUB_ISSUES_CACHE_STORAGE_KEY, JSON.stringify(map))
    }
  } catch {
    // Silently ignore — sessionStorage may be full or unavailable
  }
}

/** Caches raw sub-issues response in sessionStorage for instant restore on page refresh. */
export function cacheSubIssuesResponse(
  nwoReference: string,
  response: {nodes: Array<MemexItem>; pageInfo: PageInfo; missingSubIssues?: Array<MissingSubIssue>},
): void {
  const map = readSubIssuesCacheMap()
  map[nwoReference] = {
    nodes: response.nodes,
    pageInfo: response.pageInfo,
    missingSubIssues: response.missingSubIssues,
  }
  writeCacheMap(map)
}

/** Looks up cached sub-issues from a pre-parsed map (avoids repeated sessionStorage parses). */
export function getCachedSubIssuesQueryDataFromMap(
  map: SubIssuesCacheMap,
  nwoReference: string,
): MemexSubIssuesQueryData | undefined {
  const entry = map[nwoReference]
  if (!entry || !Array.isArray(entry.nodes)) return undefined
  return {
    nodes: entry.nodes.map(item => createMemexItemModel(item)),
    pageInfo: entry.pageInfo,
    missingSubIssues: entry.missingSubIssues,
  }
}

/** Reads cached sub-issues and reconstructs MemexItemModel instances. Returns undefined on miss or parse error. */
export function getCachedSubIssuesQueryData(nwoReference: string): MemexSubIssuesQueryData | undefined {
  return getCachedSubIssuesQueryDataFromMap(readSubIssuesCacheMap(), nwoReference)
}
